/**
 * useSpeech — browser-native text-to-speech for the copilot, built entirely on
 * the Web Speech API (`window.speechSynthesis`). NO backend / provider call:
 * the neural-TTS backend is owner-gated / offline, so spoken output must not
 * depend on it. Everything is guarded in try/catch and degrades to a no-op
 * (`supported === false`) when the API is missing, so callers can hide the UI.
 *
 * Responsibilities:
 *  - resolve a French voice from `getVoices()`, handling the async
 *    `voiceschanged` load (voices are often empty on first synchronous read);
 *  - `speak(text, {lang})` — cancel any in-progress utterance first, strip
 *    markdown / emoji noise to a clean spoken string, then speak;
 *  - `stop()` and a live `speaking` flag driven by utterance events.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface SpeakOptions {
  /** BCP-47 language tag. Defaults to 'fr-FR'. */
  lang?: string;
}

export interface UseSpeechResult {
  /** False when the Web Speech synthesis API is unavailable — hide the control. */
  supported: boolean;
  /** True while an utterance is actively being spoken. */
  speaking: boolean;
  /** Speak `text` (markdown/emoji stripped). Cancels any current utterance first. */
  speak: (text: string, options?: SpeakOptions) => void;
  /** Stop any in-progress speech immediately. */
  stop: () => void;
}

function detectSupported(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.speechSynthesis !== 'undefined' &&
      typeof window.SpeechSynthesisUtterance !== 'undefined'
    );
  } catch {
    return false;
  }
}

/**
 * Reduce markdown / emoji / layout noise to a clean, natural spoken string.
 * Order matters: pull link/image text out before stripping bracket/paren
 * punctuation, and drop emoji / symbol glyphs that a TTS engine would either
 * skip awkwardly or read literally ("visage souriant").
 */
export function cleanForSpeech(input: string): string {
  if (!input) return '';
  let text = input;
  try {
    text = text
      // fenced + inline code: keep the words, drop the backticks/fences
      .replace(/```[a-zA-Z0-9]*\n?/g, ' ')
      .replace(/`([^`]*)`/g, '$1')
      // images ![alt](url) -> alt ; links [text](url) -> text
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      // headings / blockquotes / list bullets at line start
      .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
      .replace(/^[ \t]*>[ \t]?/gm, '')
      .replace(/^[ \t]*[-*+][ \t]+/gm, '')
      // emphasis / strike markers
      .replace(/(\*\*|__|\*|_|~~)/g, '')
      // horizontal rules
      .replace(/^[ \t]*([-*_])\1{2,}[ \t]*$/gm, ' ')
      // emoji, dingbats, symbols, arrows + variation selectors / ZWJ
      .replace(
        /[\u{1F000}-\u{1FAFF}\u{1F1E6}-\u{1F1FF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{2190}-\u{21FF}\u{2300}-\u{23FF}️‍]/gu,
        '',
      )
      // collapse whitespace
      .replace(/\s+/g, ' ')
      .trim();
  } catch {
    // On any regex/engine quirk, fall back to the raw (still speakable) text.
    return input.trim();
  }
  return text;
}

export function useSpeech(): UseSpeechResult {
  const [supported] = useState<boolean>(detectSupported);
  const [speaking, setSpeaking] = useState(false);

  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Populate + keep the voice list fresh. getVoices() is frequently empty on
  // the first synchronous call; the browser fires `voiceschanged` once the
  // list is ready (and again if it changes).
  useEffect(() => {
    if (!supported) return;
    const load = () => {
      try {
        voicesRef.current = window.speechSynthesis.getVoices() ?? [];
      } catch {
        voicesRef.current = [];
      }
    };
    load();
    try {
      window.speechSynthesis.addEventListener('voiceschanged', load);
    } catch {
      /* some engines don't expose the event — the eager load() still works */
    }
    return () => {
      try {
        window.speechSynthesis.removeEventListener('voiceschanged', load);
      } catch {
        /* no-op */
      }
    };
  }, [supported]);

  const pickVoice = useCallback((lang: string): SpeechSynthesisVoice | undefined => {
    const voices = voicesRef.current;
    if (!voices.length) return undefined;
    const lower = lang.toLowerCase();
    const prefix = lower.split('-')[0];
    return (
      voices.find((v) => v.lang?.toLowerCase() === lower) ??
      voices.find((v) => v.lang?.toLowerCase().startsWith(prefix)) ??
      undefined
    );
  }, []);

  const stop = useCallback(() => {
    if (!supported) return;
    try {
      const prev = utteranceRef.current;
      if (prev) {
        // Detach handlers so the cancel-triggered onend doesn't fight a new speak().
        prev.onstart = null;
        prev.onend = null;
        prev.onerror = null;
      }
      utteranceRef.current = null;
      window.speechSynthesis.cancel();
    } catch {
      /* never throw */
    }
    setSpeaking(false);
  }, [supported]);

  const speak = useCallback(
    (text: string, options?: SpeakOptions) => {
      if (!supported) return;
      try {
        const clean = cleanForSpeech(text);
        if (!clean) return;
        // Cancel any in-progress utterance first (detaches its handlers).
        stop();

        const lang = options?.lang ?? 'fr-FR';
        const utterance = new window.SpeechSynthesisUtterance(clean);
        utterance.lang = lang;
        const voice = pickVoice(lang);
        if (voice) utterance.voice = voice;
        utterance.onstart = () => setSpeaking(true);
        utterance.onend = () => setSpeaking(false);
        utterance.onerror = () => setSpeaking(false);
        utteranceRef.current = utterance;
        setSpeaking(true);
        window.speechSynthesis.speak(utterance);
      } catch {
        setSpeaking(false);
      }
    },
    [supported, stop, pickVoice],
  );

  // Safety net: stop any speech if the hook unmounts.
  useEffect(() => stop, [stop]);

  return { supported, speaking, speak, stop };
}
